/**
 * B3 — the inbox (`MCK-20260521-009`, `chat1.md` wireframe 4).
 *
 * One place for everything asking for the reader's attention, across every project **and every
 * studio**: since D3 (2026-09-13) L0 sends no notification mail at all, so a task assigned to
 * you, a review requested of you and a mention land here and nowhere else.
 *
 * FIVE DECISIONS
 *
 * 1. **A row is a thing somebody asked of you, not an event log.** The server writes one row
 *    per (person, event, tab) and renders it from `title_snapshot` — the list reads no entity
 *    it points at, which is what keeps a cross-studio page one request rather than a fan-out
 *    of reads the caller may not even be allowed to make.
 * 2. **Four tabs, and the fourth is drawn even at zero.** *Assigned · Reviews awaiting ·
 *    Mentions · Following.* The summary always carries all four, so a tab does not disappear
 *    the moment it empties — a strip whose shape changes under you is a strip you cannot learn.
 * 3. **Opening a row marks it read; nothing marks itself read by being scrolled past.** The
 *    read state is the person's own bookkeeping and it has to agree with what they did.
 * 4. **The studio chip is the cross-studio half made visible.** A row from a company other than
 *    the one this tab is acting as says so, because *"who is asking"* is the first thing a
 *    person needs when the answer can be two different firms (the B3 addendum's own rule).
 * 5. **Loading is a skeleton, never a spinner** (`CLAUDE.md`, loading states): the shape is
 *    known before the rows are.
 *
 * AND ONE MORE, ADDED 2026-09-16 — **it has to be clearable at speed.** Owner: *"musí to byť
 * snappy… nech sa dá označiť, nech sa dá vybrať viacero, nech tam je context menu ako v projekt
 * scope, skratky, right-click, a pri kliknutí nech sa vysunie panel, kde si viem rýchlo pozrieť
 * viac."* So this screen borrows the project surfaces' own gestures rather than inventing a
 * second set: a selection with a bar over it, the same list of actions behind a right-click,
 * and keys for the hands that never leave the keyboard.
 *
 * - **Click opens the panel, it does not navigate.** A person triaging thirty rows wants to
 *   look, decide and move on; a navigation each time costs them the list. *Open* inside the
 *   panel is the navigation, deliberately one step further away.
 * - **Selecting is a tick, a `⌘`/`Ctrl`-click or `x`.** More than one selected and the bar
 *   appears with the same actions the right-click menu holds — one list, two doors
 *   (`KLQ-20260825-006` §G), because the second door is the only one that exists on a touch
 *   screen or for somebody nobody told about right-click.
 * - **The keys**: `↑`/`↓` move, `Enter` opens the panel, `r` marks read, `x` ticks, `Escape`
 *   clears. Written down here because a shortcut nobody can find is a shortcut nobody has.
 */

import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

import { Link, useNavigate } from '@tanstack/react-router';

import { Bell, X } from 'lucide-react';

import {
  Button,
  EmptyState,
  InlineError,
  Screen,
  ScreenHead,
  SkeletonRows,
  SelectionBar,
} from '@/components';
import type { MenuItem } from '@/components/MenuPopup';
import { useOptionalContextMenu } from '@/components/ContextMenu';
import { useProjectList, DEFAULT_PROJECT_SORT } from '@/features/projects';
import { useActiveWorkspaceId, useCompanies } from '@/features/session';
import { relativeTime, useLocale, useMessages } from '@/lib/i18n';
import { useInfiniteSentinel } from '@/lib/infinite';

import type { InboxItem, InboxScope, InboxTab } from './api';
import { INBOX_TABS } from './api';
import { useStudioAuthority } from '@/features/studio';
import styles from './Inbox.module.css';
import { useInboxList, useInboxSummary, useMarkInbox } from './queries';

/** TWIN: `INBOX_TABS` in `api.ts`, which is the twin of the server's own tuple. */
const TAB_LABEL: Readonly<Record<InboxTab, 'tab.assigned' | 'tab.review' | 'tab.mention' | 'tab.following'>> = {
  assigned: 'tab.assigned',
  review_awaiting: 'tab.review',
  mention: 'tab.mention',
  following: 'tab.following',
};

export function InboxPage() {
  const t = useMessages('inbox');
  const { locale } = useLocale();
  const navigate = useNavigate();

  const [tab, setTab] = useState<InboxTab>('assigned');
  /**
   * Whose inbox is on screen (owner, 2026-09-16: *"admin by mal vidieť všetko, čo sa deje, ale
   * mal by tam mať aj filter — čo sa mňa týka a čo sa ma netýka"*).
   *
   * Three states on one control, because they partition: mine, the firm's, the firm's minus
   * mine. The third is the one that keeps a lead out of the flood — their own is cleared, and
   * what is left is what everybody else still owes. Drawn only for somebody who may ask; the
   * server answers `403` to everybody else, and a control whose only outcome is a refusal is
   * not a control.
   */
  const [scope, setScope] = useState<InboxScope>('me');
  const studio = useStudioAuthority();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  /** Ticked rows. Ids rather than indexes: the list grows under the selection as pages land. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /** Which row the keys act on. `null` until somebody has moved or clicked. */
  const [focused, setFocused] = useState<string | null>(null);
  /** The row whose panel is open — a look, not a navigation. */
  const [previewId, setPreviewId] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  const contextMenu = useOptionalContextMenu();

  const list = useInboxList(tab, unreadOnly, scope);
  // The strip counts what the list shows — one scope, or the two disagree on screen.
  const summary = useInboxSummary(true, scope);
  const mark = useMarkInbox();

  // The project name is a join against the list B1 already holds, never a request per row —
  // `MyTasksPage` makes the same trade for the same reason.
  const projects = useProjectList({ status: 'all', sort: DEFAULT_PROJECT_SORT });
  const projectName = new Map(
    (projects.data?.pages ?? []).flatMap((page) =>
      page.items.map((project) => [project.id, project.name] as const),
    ),
  );

  const companies = useCompanies();
  const activeWorkspaceId = useActiveWorkspaceId();
  const studioOf = (workspaceId: string): string | null => {
    if (companies.length < 2 || workspaceId === activeWorkspaceId) return null;
    return companies.find((company) => company.id === workspaceId)?.name ?? null;
  };

  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = new Map((summary.data?.tabs ?? []).map((one) => [one.tab, one] as const));

  useInfiniteSentinel(sentinel, {
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: () => {
      void list.fetchNextPage();
    },
  });

  /**
   * Every action this screen offers, over whichever ids the gesture names.
   *
   * One function, three callers — the bar, the right-click menu and the keys — because three
   * copies of *"mark these read"* is three places for the optimistic state to drift.
   */
  const act = useCallback(
    (ids: readonly string[], change: { read?: boolean; dismissed?: boolean }): void => {
      if (ids.length === 0) return;
      mark.mutate({ item_ids: ids, ...change });
      setSelected(new Set());
    },
    [mark],
  );

  /** The action list, built once and rendered twice (`KLQ-20260825-006` §G). */
  const actionsFor = (ids: readonly string[]): MenuItem[] => {
    const rows = items.filter((one) => ids.includes(one.id));
    // A lead may read their people's mail; marking it read is interference, and the server
    // refuses it anyway (`PATCH /inbox` touches the caller's own rows and reports the rest as
    // missed). Offered as greyed rather than hidden, so the rule is visible where it applies.
    const mine = scope === 'me';
    const anyUnread = rows.some((one) => one.read_at === null);
    return [
      {
        id: 'read',
        label: t(anyUnread ? 'action.markRead' : 'action.markUnread'),
        disabled: !mine,
        reason: mine ? undefined : t('action.onlyMine'),
        ...(mine
          ? {
              onSelect: () => {
                act(ids, { read: anyUnread });
              },
            }
          : {}),
      },
      {
        id: 'dismiss',
        label: t('action.dismiss'),
        disabled: !mine,
        reason: mine ? undefined : t('action.onlyMine'),
        ...(mine
          ? {
              onSelect: () => {
                act(ids, { dismissed: true });
              },
            }
          : {}),
      },
      { id: 'sep', separator: true },
      {
        id: 'open',
        label: t('action.open'),
        // One row opens a thing; four rows open nothing, because *"open"* has no plural.
        disabled: rows.length !== 1,
        reason: rows.length === 1 ? undefined : t('action.openOne'),
        ...(rows.length === 1 && rows[0] !== undefined
          ? {
              onSelect: () => {
                const row = rows[0];
                if (row !== undefined) open(row);
              },
            }
          : {}),
      },
    ];
  };

  /** Open the thing, and mark the row read because opening it is reading it. */
  const open = (item: InboxItem): void => {
    if (item.read_at === null) mark.mutate({ item_ids: [item.id], read: true });
    if (item.project_id === null || item.entity_id === null) return;
    if (item.entity_type === 'task') {
      void navigate({
        to: '/projects/$projectId/tasks/$taskId',
        params: { projectId: item.project_id, taskId: item.entity_id },
      });
      return;
    }
    // A review opens its queue rather than a detail route, which does not exist yet
    // (`MCK-20260521-007` is not built). The queue is where the decision is taken, so it is the
    // honest destination rather than a link that 404s.
    void navigate({
      to: '/projects/$projectId/reviews',
      params: { projectId: item.project_id },
      search: {},
    });
  };

  const toggle = useCallback((id: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * The keys, bound to the list and not to the window.
   *
   * A screen that listens on `window` for `r` is a screen nobody can type an `r` into — the
   * search box two rows up is on the same page. The list is focusable, the keys act on the
   * focused row, and everything they do is also a control somebody can see.
   */
  const onKeyDown = (event: ReactKeyboardEvent<HTMLOListElement>): void => {
    if (items.length === 0) return;
    const index = focused === null ? -1 : items.findIndex((one) => one.id === focused);
    const move = (to: number): void => {
      const row = items[Math.max(0, Math.min(items.length - 1, to))];
      if (row === undefined) return;
      event.preventDefault();
      setFocused(row.id);
    };
    switch (event.key) {
      case 'ArrowDown':
        move(index + 1);
        return;
      case 'ArrowUp':
        move(index === -1 ? 0 : index - 1);
        return;
      case 'Enter': {
        const row = items[index];
        if (row === undefined) return;
        event.preventDefault();
        setPreviewId(row.id);
        if (row.read_at === null) mark.mutate({ item_ids: [row.id], read: true });
        return;
      }
      case 'r': {
        const row = items[index];
        if (row === undefined) return;
        event.preventDefault();
        act(selected.size > 0 ? [...selected] : [row.id], { read: row.read_at === null });
        return;
      }
      case 'x': {
        const row = items[index];
        if (row === undefined) return;
        event.preventDefault();
        toggle(row.id);
        return;
      }
      case 'Escape':
        if (selected.size > 0) {
          event.preventDefault();
          setSelected(new Set());
        }
        return;
      default:
        return;
    }
  };

  const preview = items.find((one) => one.id === previewId) ?? null;

  return (
    <Screen
      head={<ScreenHead level={1} title={t('page.title')} subtitle={t('page.subtitle')} />}
      toolbar={
        <div className={styles.toolbar}>
          {/* The tab strip is the filter — four of them, always all four. */}
          <div className={styles.tabs} role="tablist" aria-label={t('page.title')}>
            {INBOX_TABS.map((one) => {
              const count = counts.get(one);
              return (
                <button
                  key={one}
                  type="button"
                  role="tab"
                  aria-selected={tab === one}
                  className={[styles.tab, tab === one ? styles.tabActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setTab(one);
                  }}
                >
                  <span>{t(TAB_LABEL[one])}</span>
                  {/* The unread number, not the total: the total is how much is in the drawer,
                      the unread is what is being asked of you. Zero draws nothing rather than
                      a `0` badge, which reads as a thing to clear. */}
                  {count !== undefined && count.unread > 0 ? (
                    <span className={styles.badge}>{count.unread}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <span className={styles.spacer} />

          {studio.allowed ? (
            <div className={styles.scope} role="group" aria-label={t('scope.legend')}>
              {(['me', 'studio', 'others'] as const).map((one) => (
                <button
                  key={one}
                  type="button"
                  aria-pressed={scope === one}
                  className={[styles.scopeButton, scope === one ? styles.scopeActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    setScope(one);
                    setSelected(new Set());
                    setPreviewId(null);
                  }}
                >
                  {t(one === 'me' ? 'scope.me' : one === 'studio' ? 'scope.studio' : 'scope.others')}
                </button>
              ))}
            </div>
          ) : null}

          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(event) => {
                setUnreadOnly(event.target.checked);
              }}
            />
            <span>{t('filter.new')}</span>
          </label>

          <Button
            size="sm"
            loading={mark.isPending}
            disabled={(counts.get(tab)?.unread ?? 0) === 0}
            onClick={() => {
              // The tab, not the loaded rows: *"mark all read"* means the tab, and marking only
              // what happens to be on screen would leave a count nobody can explain.
              mark.mutate({ tabs: [tab], read: true });
            }}
          >
            {t('action.markAllRead')}
          </Button>
        </div>
      }
    >
      <div className={styles.page}>
        {mark.isError ? <InlineError variant="block">{t('error.mark')}</InlineError> : null}

        {list.isError ? (
          <InlineError variant="block">{t('error.load')}</InlineError>
        ) : list.isPending ? (
          <SkeletonRows label={t('status.loading')} rows={5} columns={[16, '50%', '20%']} />
        ) : items.length === 0 ? (
          <EmptyState
            variant="list"
            explains={t('empty.explains')}
            title={t(unreadOnly ? 'empty.new.title' : `empty.${tab}.title`)}
            body={t(unreadOnly ? 'empty.new' : `empty.${tab}`)}
          />
        ) : (
          <ol
            className={styles.list}
            ref={listRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-label={t('page.title')}
            aria-keyshortcuts="ArrowUp ArrowDown Enter r x Escape"
          >
            {items.map((item) => (
              <li key={item.id}>
                <InboxRow
                  item={item}
                  project={
                    item.project_id === null
                      ? null
                      : (projectName.get(item.project_id) ?? t('row.projectUnavailable'))
                  }
                  studio={studioOf(item.workspace_id)}
                  when={relativeTime(item.created_at, locale)}
                  selected={selected.has(item.id)}
                  focused={focused === item.id}
                  onSelect={() => {
                    toggle(item.id);
                  }}
                  onOpen={(event) => {
                    setFocused(item.id);
                    // `⌘`/`Ctrl`-click selects instead of opening — the gesture every list on
                    // this machine already has, so nobody has to be told.
                    if (event.metaKey || event.ctrlKey) {
                      toggle(item.id);
                      return;
                    }
                    setPreviewId(item.id);
                    if (item.read_at === null) mark.mutate({ item_ids: [item.id], read: true });
                  }}
                  onContextMenu={(event) => {
                    if (contextMenu === null) return;
                    event.preventDefault();
                    setFocused(item.id);
                    // A right-click inside a selection acts on the selection; outside one, on
                    // the row under the cursor — which is what every file manager does.
                    const ids = selected.has(item.id) ? [...selected] : [item.id];
                    contextMenu.open({
                      label: t('page.title'),
                      items: actionsFor(ids),
                      x: event.clientX,
                      y: event.clientY,
                      returnFocus: listRef.current,
                    });
                  }}
                  onDismiss={() => {
                    act([item.id], { dismissed: true });
                  }}
                  t={t}
                />
              </li>
            ))}
          </ol>
        )}

        {selected.size === 0 ? null : (
          <SelectionBar
            summary={t('selection.count', { count: selected.size })}
            items={actionsFor([...selected])}
            clearLabel={t('action.clearSelection')}
            moreLabel={t('action.more2')}
            onClear={() => {
              setSelected(new Set());
            }}
          />
        )}

        {preview === null ? null : (
          <InboxPreview
            item={preview}
            project={
              preview.project_id === null
                ? null
                : (projectName.get(preview.project_id) ?? t('row.projectUnavailable'))
            }
            studio={studioOf(preview.workspace_id)}
            when={relativeTime(preview.created_at, locale)}
            tabLabel={t(TAB_LABEL[preview.tab])}
            onOpen={() => {
              open(preview);
            }}
            onDismiss={() => {
              act([preview.id], { dismissed: true });
              setPreviewId(null);
            }}
            onClose={() => {
              setPreviewId(null);
            }}
            t={t}
          />
        )}

        <p className={styles.keys}>{t('keys.hint')}</p>

        {list.hasNextPage ? (
          <div ref={setSentinel} className={styles.sentinel}>
            <Button
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {t(list.isFetchingNextPage ? 'action.loadingMore' : 'action.more')}
            </Button>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}

function InboxRow({
  item,
  project,
  studio,
  when,
  selected,
  focused,
  onSelect,
  onOpen,
  onContextMenu,
  onDismiss,
  t,
}: {
  readonly item: InboxItem;
  readonly project: string | null;
  readonly studio: string | null;
  readonly when: string;
  readonly selected: boolean;
  readonly focused: boolean;
  readonly onSelect: () => void;
  readonly onOpen: (event: ReactMouseEvent) => void;
  readonly onContextMenu: (event: ReactMouseEvent) => void;
  readonly onDismiss: () => void;
  readonly t: (
    key: 'action.dismiss' | 'row.new' | 'row.select',
    values?: Record<string, string | number>,
  ) => string;
}) {
  const unread = item.read_at === null;
  return (
    <article
      className={[
        styles.row,
        unread ? styles.rowUnread : '',
        selected ? styles.rowSelected : '',
        focused ? styles.rowFocused : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onContextMenu={onContextMenu}
    >
      {/* The tick is its own column and its own hit target: a row that selects when you meant
          to read it is worse than one more control. */}
      <label className={styles.pick}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          aria-label={t('row.select', { title: item.title_snapshot })}
        />
      </label>

      <span className={styles.dot} aria-hidden={!unread}>
        {unread ? <span className={styles.dotOn} /> : null}
        {unread ? <span className="sr-only">{t('row.new')}</span> : null}
      </span>

      <button type="button" className={styles.rowMain} onClick={onOpen}>
        <span className={styles.rowTitle}>{item.title_snapshot}</span>
        <span className={styles.rowMeta}>
          {[project, studio].filter((one): one is string => one !== null).join(' · ')}
        </span>
      </button>

      <span className={styles.rowWhen}>{when}</span>

      <button
        type="button"
        className={styles.rowDismiss}
        aria-label={t('action.dismiss')}
        title={t('action.dismiss')}
        onClick={onDismiss}
      >
        <X size={14} strokeWidth={1.5} aria-hidden />
      </button>
    </article>
  );
}

/**
 * The panel a click opens — *"nech si viem rýchlo pozrieť viac"* (owner, 2026-09-16).
 *
 * **It shows what the row already carries and asks the server for nothing.** The inbox row is
 * deliberately self-contained (`title_snapshot`, the tab, the project, the time), so a panel
 * that fetched the task behind it would turn a triage pass over thirty rows into thirty reads
 * — and several of them would be reads on projects in another studio, which is exactly the
 * fan-out the row's shape exists to avoid.
 *
 * What it therefore holds is *why this reached you*, which is the question a person actually
 * has in front of a list of thirty things, plus the two acts that answer it: open the thing, or
 * clear it. The richer quick actions the owner has in mind — reassign, call, send — wait for
 * him to name them; a panel full of guessed buttons is worse than one with the two that are
 * certainly right.
 */
function InboxPreview({
  item,
  project,
  studio,
  when,
  tabLabel,
  onOpen,
  onDismiss,
  onClose,
  t,
}: {
  readonly item: InboxItem;
  readonly project: string | null;
  readonly studio: string | null;
  readonly when: string;
  readonly tabLabel: string;
  readonly onOpen: () => void;
  readonly onDismiss: () => void;
  readonly onClose: () => void;
  readonly t: (
    key:
      | 'panel.title'
      | 'panel.close'
      | 'panel.tab'
      | 'panel.when'
      | 'panel.project'
      | 'panel.studio'
      | 'action.open'
      | 'action.dismiss',
  ) => string;
}) {
  return (
    <aside className={styles.panel} aria-label={t('panel.title')}>
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle}>{item.title_snapshot}</h3>
        <button
          type="button"
          className={styles.rowDismiss}
          aria-label={t('panel.close')}
          onClick={onClose}
        >
          <X size={14} strokeWidth={1.5} aria-hidden />
        </button>
      </div>

      <dl className={styles.panelFacts}>
        <dt>{t('panel.tab')}</dt>
        <dd>{tabLabel}</dd>
        <dt>{t('panel.when')}</dt>
        <dd>{when}</dd>
        {project === null ? null : (
          <>
            <dt>{t('panel.project')}</dt>
            <dd>{project}</dd>
          </>
        )}
        {studio === null ? null : (
          <>
            <dt>{t('panel.studio')}</dt>
            <dd>{studio}</dd>
          </>
        )}
      </dl>

      <div className={styles.panelActions}>
        <Button size="sm" variant="primary" onClick={onOpen}>
          {t('action.open')}
        </Button>
        <Button size="sm" onClick={onDismiss}>
          {t('action.dismiss')}
        </Button>
      </div>
    </aside>
  );
}

/** The bell, for the app chrome. Its own export so the shell does not import a whole screen. */
export function InboxBell({ enabled }: { readonly enabled: boolean }) {
  const t = useMessages('inbox');
  const summary = useInboxSummary(enabled);
  const unread = summary.data?.unread ?? 0;
  return (
    <Link to="/inbox" className={styles.bell} title={t('page.title')}>
      <span className="sr-only">
        {unread === 0 ? t('page.title') : t('bell.new', { count: unread })}
      </span>
      <Bell size={18} strokeWidth={1.5} aria-hidden />
      {unread > 0 ? <span className={styles.bellCount}>{unread > 99 ? '99+' : unread}</span> : null}
    </Link>
  );
}
